<template>
  <svg class="gmf-icon" aria-hidden="true" :class="{ 'gmf-icon-safari-hack': useSafariHack, 'gmf-disabled': disabled }">
    <use :xlink:href="iconRef"></use>
  </svg>
</template>

<script>
import { memoize } from '@gamifiera/array-cache';

const defContainer = document.createElement('svg');
defContainer.style.position = 'absolute';
defContainer.style.visibility = 'hidden';
defContainer.style.width = '0';
defContainer.style.height = '0';
document.body.appendChild(defContainer);

const defsEl = document.createElement('defs');
defContainer.appendChild(defsEl);

const defs = {};
const promiseDefs = {};

function bakeStylesIntoSVG(svgNode) {
  // Extract styles from <style> tags
  const styleTags = svgNode.querySelectorAll('style');
  styleTags.forEach(styleTag => {
    const cssText = styleTag.textContent;

    // Basic parsing; assumes simple styles like "selector { property: value; }"
    const styleRules = cssText.split('}').filter(rule => rule.includes('{'));

    styleRules.forEach(rule => {
      const parts = rule.split('{');
      const selector = parts[0].trim();
      const properties = parts[1].trim();

      // Apply the styles as inline styles to the elements matching the selector
      const elements = svgNode.querySelectorAll(selector);
      elements.forEach(element => {
        element.setAttribute('style', properties);
      });
    });

    // Remove the original <style> tag
    styleTag.remove();
  });
}

const fetchIconFromUrl = memoize(async function (url) {
  const res = await fetch(url, {
    cache: 'force-cache',
  });
  const svgText = await res.text();
  return svgText;
}, 600); // No need for a super long cache, this is just for deduplicating requests when two different icons share the same url

function fetchAndEmbedSVG(url, icon) {
  if (defs[icon]) {
    return;
  }
  if (promiseDefs[icon]) {
    return promiseDefs[icon];
  }
  const promise = (async () => {
    const svgText = await fetchIconFromUrl(url);
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
    const svgEl = svgDoc.querySelector('svg');
    if (! svgEl) {
      console.error('Could not find svg element in', icon, url);
      return;
    }

    bakeStylesIntoSVG(svgEl);

    // If no viewbox
    if (!svgEl.getAttribute('viewBox')) {
      const width = svgEl.getAttribute('width');
      const height = svgEl.getAttribute('height');
      svgEl.setAttribute('viewBox', `0 0 ${width} ${height}`);
    }

    svgEl.querySelectorAll('[id*="gmf-magic"]').forEach(el => {
      let id = el.id;
      // unset element id
      el.removeAttribute('id');
      id = id.replaceAll('_x28_', '(');
      id = id.replaceAll('_x29_', ')');
      id = id.replaceAll('_x2C_', ',');
      id = id.replaceAll('_x3B_', ';');
      id = id.replaceAll('_x5F_', '_');
      id = id.replaceAll('_', ' ');

      const regexPattern = /gmf-magic\(((?:\([^()]*\)|.)*)\)/gm;
      let gmfMagicStyling = id.matchAll(regexPattern).next().value[1];

      const cssVariables = /var\(([a-z-0-9,\s]+)\)/gm;
      [...gmfMagicStyling.matchAll(cssVariables)].forEach(variable => {
        gmfMagicStyling = gmfMagicStyling.replace(variable[0], 'var(--gmf-icon-color)');
      });

      let style = el.getAttribute('style');
      if (style) {
        el.setAttribute('style', style + ' ' + gmfMagicStyling);
      } else {
        el.setAttribute('style', gmfMagicStyling);
      }
    });

    svgEl.querySelectorAll('*').forEach(el => {
      try {
        if (el.style) {
          if (el.style.fill && el.style.fill !== 'none') {
            el.style.fill = 'var(--gmf-icon-color)';
          }
          if (el.style.stroke && el.style.stroke !== 'none') {
            el.style.stroke = 'var(--gmf-icon-color)';
          }
        }
        if (el.getAttribute('fill') && el.getAttribute('fill') !== 'none') {
          el.setAttribute('fill', 'var(--gmf-icon-color)');
        }
        if (el.getAttribute('stroke') && el.getAttribute('stroke') !== 'none') {
          el.setAttribute('stroke', 'var(--gmf-icon-color)');
        }

        el.removeAttribute('id');
        el.removeAttribute('class');
      } catch (e) {
        console.error('Error setting styles for', el, e);
      }
    });

    // Unset width/height    
    svgEl.removeAttribute('width');
    svgEl.removeAttribute('height');

    svgEl.setAttribute('id', `gmf-icon-${icon}`);
    svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svgEl.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink'); // Required for safari?
    defsEl.appendChild(svgEl); // Or wherever you want it to be appended

    defs[icon] = svgEl;
  })();

  promiseDefs[icon] = promise;

  return promise;
}

import isSafari from '@/utils/isSafari';
import eventbus from '@/utils/eventbus';

export default {
  props: ['icon', 'disabled'],
  data() {
    return {
      useSafariHack: isSafari,
    };
  },
  computed: {
    iconIsUrl() {
      return this.icon && this.icon.startsWith('http');
    },
    iconId() {
      if (this.iconIsUrl) {
        return this.icon.split('/').pop().split('.')[0];
      }
      return this.icon;
    },
    iconRef() {
      return `#gmf-icon-${this.iconId}`;
    },
  },
  mounted () {
    this.$nextTick(() => {
      this.loadIcon(true);
    });

    eventbus.on('reloadIcons', this.reloadIcon);
  },
  beforeUnmount () {
    eventbus.off('reloadIcons', this.reloadIcon);
  },
  methods: {
    async reloadIcon () {
      let url = this.getIconUrl();
      const icon = this.iconId;

      if (promiseDefs[icon]) {
        delete promiseDefs[icon];
      }
      
      if (defs[icon]) {
        defsEl.removeChild(defs[icon]);
        delete defs[icon];
      }
      await fetchIconFromUrl.refresh(url);
      await this.loadIcon(true);
    },
    getIconUrl () {
      if (this.iconIsUrl) {
        return this.icon;
      }
      if (! window.document.contains(this.$el)) {
        return null;
      }
      const varName = `--gmf-icon-${this.icon}`;
      // get variable on this.$el
      // @TODO we should check this.$el
      // in case we update the icon variable for a subset of elements
      // However this might be a cause of some issues on safari, besides
      // we set the id of the icon element to the icon name so we cant have two variants yet
      // either way
      const style = getComputedStyle(document.body);
      let url = style.getPropertyValue(varName).trim().replace(/^url\(['"]?/, '').replace(/['"]?\)$/, '');

      url = url.replaceAll('\\', ''); // Safari url fix

      if (!url) {
        console.error('Did not get an icon url for icon ' + this.icon + ', skipping', this.$el);
        return null;
      }

      return url;
    },
    async loadIcon(retry = false) {
      let icon = this.iconId;
      let url = this.getIconUrl();

      if (!icon || !url) {
        if (retry) {
          setTimeout(() => {
            this.loadIcon(false);
          }, 10);
        }
        return;
      }

      await fetchAndEmbedSVG(url, icon);
    },
  },
  watch: {
    icon() {
      this.loadIcon(true);
    },
  },
};
</script>


<style>
.gmf-icon {
  width: 18px;
  height: 18px;
  display: inline-block;
  padding: 0;
  line-height: 0 !important;
}

.gmf-icon-safari-hack {
  transform: translateZ(0);
}

.gmf-icon.gmf-disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
}
</style>
